import { hashKey, MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { isClientError, isUnauthenticated } from "./lib/api-error";
import { forgetSession, sessionQuery } from "./lib/session";
import { routeTree } from "./routeTree.gen";
import "./lib/i18n";

/**
 * When any call answers "no session" (it ran out, or someone signed out elsewhere), forget
 * everything cached and go to the login page, which brings the employee back here afterwards. The
 * session query itself is left to the `_admin` route, which redirects on its own.
 */
const sessionEnded = (error: unknown, queryKey?: readonly unknown[]) => {
	if (!isUnauthenticated(error)) return;
	if (queryKey && hashKey(queryKey) === hashKey(sessionQuery().queryKey)) return;
	forgetSession(queryClient);
	void router.navigate({ to: "/login", search: { redirect: router.state.location.href } });
};

const queryClient = new QueryClient({
	queryCache: new QueryCache({ onError: (error, query) => sessionEnded(error, query.queryKey) }),
	mutationCache: new MutationCache({ onError: (error) => sessionEnded(error) }),
	defaultOptions: {
		queries: {
			// Links preload on hover; a little freshness spares the second fetch on the click.
			staleTime: 10_000,
			// A 4xx will answer the same next time; only a network or server error is worth a retry.
			retry: (failures, error) => !isClientError(error) && failures < 3,
		},
	},
});

const router = createRouter({
	routeTree,
	context: { queryClient },
	defaultPreload: "intent",
	// Loaders read through the query cache (`ensureQueryData`), which decides freshness.
	defaultPreloadStaleTime: 0,
	scrollRestoration: true,
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

const root = document.getElementById("app");
if (!root) throw new Error("index.html has no #app element");

createRoot(root).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			<RouterProvider router={router} />
		</QueryClientProvider>
	</StrictMode>,
);
